// src/admin-components/AdminServiceAnalytics.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Topbar  from "./Topbar";
import {
  adminAnalyticsApi, adminGetServicesApi,
  adminToggleServiceApi, adminCreateServiceApi, adminUpdateServiceApi,
} from "../api/bookingApi";
import "./admin-dashboard.css";
import "./admin-bookings.css";

const STATUS_COLOR = {
  pending_payment:"#F59E0B", payment_failed:"#EF4444",
  awaiting_confirmation:"#3B82F6", confirmed:"#10B981",
  in_progress:"#8B5CF6", completed:"#059669", cancelled:"#9CA3AF",
};
const STATUS_LABEL = {
  pending_payment:"Pending Payment", payment_failed:"Payment Failed",
  awaiting_confirmation:"Awaiting Confirm", confirmed:"Confirmed",
  in_progress:"In Progress", completed:"Completed", cancelled:"Cancelled",
};

const CATEGORIES = ["Medical","Companion","Emergency","Nursing","Physiotherapy","Housekeeping","Transport","Other"];

const EMPTY_FORM = { name:"", category:"Medical", description:"", price:"", duration:"" };

// ── Service Form Modal ────────────────────────────────────────────────────────
function ServiceModal({ service, onClose, onSaved }) {
  const isEdit = !!service?.id;
  const [form, setForm]     = useState(isEdit ? {
    name:        service.name,
    category:    service.category,
    description: service.description || "",
    price:       service.price,
    duration:    service.duration || "",
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim())     return setError("Service name is required");
    if (!form.category)        return setError("Category is required");
    if (form.price === "")     return setError("Price is required");
    if (isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0)
      return setError("Price must be a valid number (0 for free)");

    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await adminUpdateServiceApi(service.id, form);
      } else {
        await adminCreateServiceApi(form);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    padding: "10px 12px", borderRadius: 10, border: "1px solid #CBD5E1",
    fontSize: 14, width: "100%", boxSizing: "border-box", fontFamily: "inherit",
    outline: "none",
  };

  return (
    <>
      <div onClick={onClose} style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,.45)",
        zIndex:2000, backdropFilter:"blur(3px)"
      }}/>
      <div style={{
        position:"fixed", top:"50%", left:"50%",
        transform:"translate(-50%,-50%)",
        background:"#fff", borderRadius:20, padding:32, width:"100%",
        maxWidth:500, zIndex:2001, boxShadow:"0 30px 80px rgba(0,0,0,.25)",
        maxHeight:"90vh", overflowY:"auto"
      }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:"#1E293B" }}>
              {isEdit ? "✏️ Edit Service" : "➕ Add New Service"}
            </h2>
            <p style={{ margin:0, fontSize:13, color:"#64748B" }}>
              {isEdit ? "Update service details & pricing" : "Add a new service to the catalogue"}
            </p>
          </div>
          <button onClick={onClose} style={{
            background:"#F1F5F9", border:"none", width:36, height:36,
            borderRadius:"50%", cursor:"pointer", fontSize:16, color:"#64748B"
          }}>✕</button>
        </div>

        {error && (
          <div style={{
            background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10,
            padding:"10px 14px", color:"#DC2626", fontSize:14, marginBottom:16
          }}>⚠️ {error}</div>
        )}

        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Name */}
          <div>
            <label style={{ fontSize:13, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>
              Service Name *
            </label>
            <input style={inp} placeholder="e.g. Doctor Home Visit"
              value={form.name} onChange={e => set("name", e.target.value)} />
          </div>

          {/* Category */}
          <div>
            <label style={{ fontSize:13, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>
              Category *
            </label>
            <select style={{ ...inp, background:"#fff", cursor:"pointer" }}
              value={form.category} onChange={e => set("category", e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Price + Duration row */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={{ fontSize:13, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>
                Price (₹) * <span style={{ fontSize:11, color:"#94A3B8" }}>(0 = FREE)</span>
              </label>
              <input style={inp} type="number" min="0" step="0.01"
                placeholder="e.g. 999"
                value={form.price} onChange={e => set("price", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:13, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>
                Duration <span style={{ fontSize:11, color:"#94A3B8" }}>(optional)</span>
              </label>
              <input style={inp} placeholder="e.g. 1 hour"
                value={form.duration} onChange={e => set("duration", e.target.value)} />
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize:13, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>
              Description <span style={{ fontSize:11, color:"#94A3B8" }}>(optional)</span>
            </label>
            <textarea style={{ ...inp, resize:"vertical", minHeight:80 }}
              placeholder="Brief description of the service..."
              value={form.description} onChange={e => set("description", e.target.value)} />
          </div>

          {/* Price preview */}
          {form.price !== "" && !isNaN(parseFloat(form.price)) && (
            <div style={{
              background:"#EFF6FF", border:"1px solid #BFDBFE",
              borderRadius:10, padding:"10px 14px", fontSize:13, color:"#1D4ED8"
            }}>
              💰 Price preview: <strong>
                {parseFloat(form.price) === 0 ? "FREE" : `₹${parseFloat(form.price).toLocaleString("en-IN")}`}
              </strong>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <button onClick={onClose} disabled={saving} style={{
              flex:1, padding:"12px", borderRadius:12, border:"1px solid #CBD5E1",
              background:"#fff", cursor:"pointer", fontWeight:700, fontSize:14, color:"#64748B"
            }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving} style={{
              flex:2, padding:"12px", borderRadius:12, border:"none",
              background: saving ? "#93C5FD" : "var(--base-primary)",
              color:"#fff", cursor: saving ? "not-allowed" : "pointer",
              fontWeight:800, fontSize:14
            }}>
              {saving ? "Saving..." : isEdit ? "💾 Save Changes" : "➕ Add Service"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 560px) {
          .sa-kpi { grid-template-columns: repeat(4,1fr) !important; }
        }
        @media (max-width: 560px) {
          .sa-kpi > div { padding: 14px 12px !important; }
          .sa-kpi > div > div:nth-child(2) { font-size: 20px !important; }
        }
      `}</style>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminServiceAnalytics() {
  const [data,      setData]      = useState(null);
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("overview");
  const [modal,     setModal]     = useState(null);   // null | "add" | serviceObj
  const [catFilter, setCatFilter] = useState("All");
  const [searchSvc, setSearchSvc] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([adminAnalyticsApi(), adminGetServicesApi()]);
      setData(aRes.data);
      setServices(sRes.data.services);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleSvc = async (id) => { await adminToggleServiceApi(id); load(); };
  const fmtMoney  = (n) => `₹${parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits:0 })}`;

  const maxBookings = data?.by_service?.length ? Math.max(...data.by_service.map(s=>s.total_bookings), 1) : 1;
  const maxRevenue  = data?.monthly?.length    ? Math.max(...data.monthly.map(m=>m.revenue), 1) : 1;

  // Filtered services
  const allCats = ["All", ...new Set(services.map(s => s.category))];
  const filteredServices = services.filter(s => {
    const matchCat  = catFilter === "All" || s.category === catFilter;
    const matchSearch = !searchSvc || s.name.toLowerCase().includes(searchSvc.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) return (
    <div className="adm-layout">
      <Sidebar active="analytics"/>
      <main className="adm-main">
        <div className="adm-shell">
          <div style={{ padding:40, color:"#94A3B8", textAlign:"center" }}>Loading analytics...</div>
        </div>
      </main>
    </div>
  );

  return (
    <div className="adm-layout">
      <Sidebar active="analytics"/>
      <main className="adm-main">
        <div className="adm-shell">
          <Topbar title="Service Analytics" subtitle="Bookings, revenue & manage services"/>

          {/* TAB TOGGLE */}
          <div style={{ display:"flex", gap:8, marginBottom:24 }}>
            {[["overview","📊 Overview"],["services","🛠 Manage Services"]].map(([k,l]) => (
              <button key={k} onClick={() => setTab(k)} style={{
                padding:"10px 20px", borderRadius:10, border:"none", cursor:"pointer",
                fontWeight:700, fontSize:14,
                background: tab === k ? "var(--base-primary)" : "#F1F5F9",
                color: tab === k ? "#fff" : "#64748B",
              }}>{l}</button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ── */}
          {tab === "overview" && data && (
            <>
              {/* KPI Cards */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginBottom:20, className:"sa-kpi" }}>
                {[
                  { icon:"📋", label:"Total Bookings",        val: data.by_status.reduce((a,b)=>a+parseInt(b.count),0), bg:"#EFF6FF", c:"#1D4ED8" },
                  { icon:"⏳", label:"Awaiting Confirmation", val: data.pending_confirmations, bg:"#FFF7ED", c:"#C2410C" },
                  { icon:"₹",  label:"Total Revenue",         val: fmtMoney(data.total_revenue), bg:"#F0FDF4", c:"#15803D" },
                  { icon:"📅", label:"Today's Bookings",      val: data.today_bookings, bg:"#FAF5FF", c:"#7E22CE" },
                ].map((k,i) => (
                  <div key={i} style={{ background:k.bg, border:`1px solid ${k.c}22`, borderRadius:14, padding:"18px 20px" }}>
                    <div style={{ fontSize:28, marginBottom:6 }}>{k.icon}</div>
                    <div style={{ fontSize:24, fontWeight:800, color:k.c }}>{k.val}</div>
                    <div style={{ fontSize:13, color:"#64748B", fontWeight:600 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:16, marginBottom:16 }}>
                {/* Bookings by Service */}
                <div className="adm-panel">
                  <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800 }}>📊 Bookings by Service</h3>
                  {data.by_service.map((s,i) => (
                    <div key={i} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                        <span style={{ fontWeight:700 }}>{s.name}</span>
                        <span style={{ color:"#64748B" }}>{s.total_bookings} · {fmtMoney(s.revenue)}</span>
                      </div>
                      <div style={{ height:8, background:"#E2E8F0", borderRadius:999, overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:999, background:"var(--base-primary)", width:`${(s.total_bookings/maxBookings)*100}%` }}/>
                      </div>
                    </div>
                  ))}
                  {data.by_service.length === 0 && <div style={{ color:"#94A3B8", fontSize:14 }}>No bookings yet.</div>}
                </div>

                {/* By Status */}
                <div className="adm-panel">
                  <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800 }}>🔢 Bookings by Status</h3>
                  {data.by_status.map((s,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      <div style={{ width:10, height:10, borderRadius:"50%", background:STATUS_COLOR[s.status]||"#94A3B8", flexShrink:0 }}/>
                      <span style={{ fontSize:13, flex:1 }}>{STATUS_LABEL[s.status]||s.status}</span>
                      <div style={{ flex:2, height:8, background:"#E2E8F0", borderRadius:999, overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:999, background:STATUS_COLOR[s.status]||"#94A3B8",
                          width:`${(s.count/data.by_status.reduce((a,b)=>a+parseInt(b.count),0))*100}%` }}/>
                      </div>
                      <span style={{ fontWeight:700, fontSize:13, minWidth:24 }}>{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Trend */}
              <div className="adm-panel">
                <h3 style={{ margin:"0 0 20px", fontSize:15, fontWeight:800 }}>📈 Monthly Trend (Last 6 Months)</h3>
                {data.monthly.length === 0 ? (
                  <div style={{ color:"#94A3B8", fontSize:14 }}>No monthly data yet.</div>
                ) : (
                  <div style={{ display:"flex", gap:16, alignItems:"flex-end", height:180 }}>
                    {data.monthly.map((m,i) => (
                      <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                        <div style={{ fontSize:12, color:"#64748B", fontWeight:600 }}>{fmtMoney(m.revenue)}</div>
                        <div style={{
                          width:"100%", borderRadius:"6px 6px 0 0",
                          background:"var(--base-primary)",
                          height:`${Math.max((m.revenue/maxRevenue)*140, 4)}px`,
                          transition:"height .3s"
                        }} title={`${m.bookings} bookings`}/>
                        <div style={{ fontSize:12, fontWeight:700, color:"#1E293B" }}>{m.month}</div>
                        <div style={{ fontSize:11, color:"#94A3B8" }}>{m.bookings} bk</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── MANAGE SERVICES TAB ── */}
          {tab === "services" && (
            <div className="adm-panel">
              {/* Header row */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>🛠 Service Catalogue</h3>
                  <p style={{ margin:"4px 0 0", fontSize:13, color:"#64748B" }}>{services.length} services total</p>
                </div>
                <button onClick={() => setModal("add")} style={{
                  padding:"10px 20px", borderRadius:10, border:"none",
                  background:"var(--base-primary)", color:"#fff",
                  cursor:"pointer", fontWeight:800, fontSize:14
                }}>
                  ➕ Add New Service
                </button>
              </div>

              {/* Filters */}
              <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                <input
                  value={searchSvc} onChange={e => setSearchSvc(e.target.value)}
                  placeholder="Search services..."
                  style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #CBD5E1", fontSize:14, width:220 }}
                />
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {allCats.map(c => (
                    <button key={c} onClick={() => setCatFilter(c)} style={{
                      padding:"6px 14px", borderRadius:20, border:"none", cursor:"pointer",
                      fontWeight:600, fontSize:13,
                      background: catFilter === c ? "var(--base-primary)" : "#F1F5F9",
                      color: catFilter === c ? "#fff" : "#64748B"
                    }}>{c}</button>
                  ))}
                </div>
              </div>

              {/* Services Grid */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px,1fr))", gap:16 }}>
                {filteredServices.map(s => (
                  <div key={s.id} style={{
                    border: s.is_active ? "1px solid #E2E8F0" : "1px dashed #CBD5E1",
                    borderRadius:14, padding:"18px 20px", background: s.is_active ? "#fff" : "#F8FAFC",
                    opacity: s.is_active ? 1 : 0.7,
                    display:"flex", flexDirection:"column", gap:10
                  }}>
                    {/* Top row */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:800, fontSize:15, color:"#1E293B", marginBottom:2 }}>{s.name}</div>
                        <span style={{
                          fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:999,
                          background:"#EFF6FF", color:"#1D4ED8"
                        }}>{s.category}</span>
                      </div>
                      <span style={{
                        fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:999,
                        background: s.is_active ? "#DCFCE7" : "#FEE2E2",
                        color: s.is_active ? "#16A34A" : "#DC2626"
                      }}>{s.is_active ? "Active" : "Inactive"}</span>
                    </div>

                    {/* Description */}
                    {s.description && (
                      <p style={{ margin:0, fontSize:13, color:"#64748B", lineHeight:1.5 }}>{s.description}</p>
                    )}

                    {/* Price + Duration */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ fontSize:22, fontWeight:900, color:"var(--base-primary)" }}>
                        {parseFloat(s.price) === 0 ? <span style={{ color:"#16A34A" }}>FREE</span> : fmtMoney(s.price)}
                      </div>
                      {s.duration && (
                        <div style={{ fontSize:13, color:"#64748B", background:"#F1F5F9", padding:"4px 10px", borderRadius:8 }}>
                          ⏱ {s.duration}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display:"flex", gap:8, marginTop:4 }}>
                      <button onClick={() => setModal(s)} style={{
                        flex:1, padding:"8px", borderRadius:8, border:"1px solid #CBD5E1",
                        background:"#fff", cursor:"pointer", fontWeight:700, fontSize:13, color:"#1E293B"
                      }}>✏️ Edit</button>
                      <button onClick={() => toggleSvc(s.id)} style={{
                        flex:1, padding:"8px", borderRadius:8, border:"none", cursor:"pointer",
                        fontWeight:700, fontSize:13,
                        background: s.is_active ? "#FEE2E2" : "#DCFCE7",
                        color: s.is_active ? "#DC2626" : "#16A34A"
                      }}>{s.is_active ? "Disable" : "Enable"}</button>
                    </div>
                  </div>
                ))}

                {filteredServices.length === 0 && (
                  <div style={{ gridColumn:"1/-1", textAlign:"center", color:"#94A3B8", padding:40, fontSize:15 }}>
                    No services found. Try adjusting the filters.
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Add / Edit Modal */}
      {modal && (
        <ServiceModal
          service={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}